'use client';

import { useState, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ImageComparisonSlider } from '@/shared/ui/ImageComparisonSlider';
import {
  ImageViewer,
  viewerRectFromElement,
  type ViewerSourceRect,
} from '@/shared/ui';
import { Badge } from '@/shared/ui/shadcn/badge';
import { useTouchSwipe } from '@/shared/hooks/use-touch-swipe';
import {
  computeImageContainerStyle,
  clampAspectRatio,
  type ImageSizingConstraints,
} from '@/shared/hooks';

interface AngleCarouselWithSliderProps {
  sourceUrl?: string;
  renderUrls: (string | undefined)[];
  alt: string;
  currentIndex: number;
  onIndexChange: (index: number) => void;
  sliderPosition?: number;
  onSliderPositionChange?: (position: number) => void;
  isTransitioning?: boolean;
  containerClassName?: string;
  showNavigation?: boolean;
  isUpscaled?: boolean;
  /** Whether a render is actively in progress (applies grayscale to image) */
  isRendering?: boolean;
  onDraggingChange?: (isDragging: boolean) => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  /** Called when user clicks on the image (to open viewer externally) */
  onImageClick?: () => void;
  /** External control for viewer open state */
  isViewerOpenExternal?: boolean;
  /** Callback when viewer is closed (for external control) */
  onViewerClose?: () => void;
  /** Per-layout sizing constraints (Apple-parity caps/floors/lift). */
  sizing: ImageSizingConstraints;
  /** External programmatic slider position — drives the compare slider
   *  to a target position when render-form mode changes (two-column mode). */
  sliderProgrammaticPosition?: number;
  /** Angle aspect ratio (width / height) known from the API before the image
   *  loads. Seeds the container shape so it doesn't reflow from the 16:9 default
   *  once the browser decodes the image. */
  initialAspectRatio?: number;
}

export function AngleCarouselWithSlider({
  sourceUrl,
  renderUrls,
  alt,
  currentIndex,
  onIndexChange,
  sliderPosition: controlledSliderPosition,
  onSliderPositionChange,
  isTransitioning: externalIsTransitioning = false,
  containerClassName = '',
  showNavigation = true,
  isUpscaled = false,
  isRendering = false,
  onDraggingChange,
  onSwipeLeft,
  onSwipeRight,
  onImageClick,
  isViewerOpenExternal,
  onViewerClose,
  sizing,
  sliderProgrammaticPosition,
  initialAspectRatio,
}: AngleCarouselWithSliderProps) {
  const tCommon = useTranslations('common');
  const [dimensions, setDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  // On-screen rect of the image frame at tap time — drives the viewer hero morph.
  const [sourceRect, setSourceRect] = useState<ViewerSourceRect | null>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const [localSliderPosition, setLocalSliderPosition] = useState(
    controlledSliderPosition ?? 0
  );
  const [localIsTransitioning, setLocalIsTransitioning] = useState(false);
  const [isDraggingSlider, setIsDraggingSlider] = useState(false);
  const [wasRecentlyDragging, setWasRecentlyDragging] = useState(false);
  const positionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const draggingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Available column width (full-width outer div) — feeds the landscape lift.
  const [cardWidth, setCardWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setCardWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Use external transition state if provided, otherwise use local
  const isTransitioning = externalIsTransitioning || localIsTransitioning;

  // Always use local state for smooth movement, only sync on unmount or significant changes
  const sliderPosition = localSliderPosition;

  const handleSliderPositionChange = (position: number) => {
    // Update local state immediately for smooth movement
    setLocalSliderPosition(position);

    // Debounce the parent update to avoid performance issues
    if (onSliderPositionChange) {
      if (positionTimeoutRef.current) {
        clearTimeout(positionTimeoutRef.current);
      }
      positionTimeoutRef.current = setTimeout(() => {
        onSliderPositionChange(position);
      }, 100); // Update parent state after user stops moving for 100ms
    }
  };

  const handleDraggingChange = (dragging: boolean) => {
    setIsDraggingSlider(dragging);
    onDraggingChange?.(dragging);

    // When dragging ends, add a short delay before re-enabling swipe
    // This prevents the touchend event from being interpreted as a swipe
    if (!dragging) {
      setWasRecentlyDragging(true);

      // Clear any existing timeout
      if (draggingTimeoutRef.current) {
        clearTimeout(draggingTimeoutRef.current);
      }

      // Re-enable swipe after 200ms
      draggingTimeoutRef.current = setTimeout(() => {
        setWasRecentlyDragging(false);
      }, 200);
    } else {
      // If starting to drag, immediately disable swipe
      setWasRecentlyDragging(false);
      if (draggingTimeoutRef.current) {
        clearTimeout(draggingTimeoutRef.current);
        draggingTimeoutRef.current = null;
      }
    }
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (positionTimeoutRef.current) {
        clearTimeout(positionTimeoutRef.current);
      }
      if (draggingTimeoutRef.current) {
        clearTimeout(draggingTimeoutRef.current);
      }
    };
  }, []);

  // Sync with controlled position when it changes significantly (e.g., when switching angles)
  useEffect(() => {
    if (
      controlledSliderPosition !== undefined &&
      Math.abs(controlledSliderPosition - localSliderPosition) > 1
    ) {
      setLocalSliderPosition(controlledSliderPosition);
    }
  }, [controlledSliderPosition]); // localSliderPosition intentionally not included to avoid loop

  // Filter out undefined URLs
  const validRenderUrls = renderUrls.filter((url) => url) as string[];

  // Get current render URL
  const currentRenderUrl = validRenderUrls[currentIndex];

  // Get dimensions from the source image
  useEffect(() => {
    if (!sourceUrl) return;

    const img = new window.Image();
    img.onload = () => {
      setDimensions({ width: img.width, height: img.height });
    };
    img.onerror = () => {
      setDimensions(null);
    };
    img.src = sourceUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [sourceUrl]);

  // Aspect ratio (clamped 3:4..16:9). Prefer the measured size; before it loads,
  // fall back to the API-known angle aspect so the container is correctly shaped
  // on the first render (no reflow). Default 16:9 when neither is available.
  const aspectRatio = clampAspectRatio(
    dimensions
      ? dimensions.width / dimensions.height
      : (initialAspectRatio ?? null)
  );

  // Container style — Apple-parity caps/floors + single-column landscape lift.
  const { style: containerStyle } = computeImageContainerStyle(
    aspectRatio,
    sizing,
    cardWidth
  );

  // Add swipe support for navigating renders (if callbacks provided)
  // Swipe is disabled when dragging the comparison slider or shortly after
  useTouchSwipe({
    onSwipeLeft: onSwipeLeft,
    onSwipeRight: onSwipeRight,
    enabled:
      !isDraggingSlider &&
      !wasRecentlyDragging &&
      (!!onSwipeLeft || !!onSwipeRight),
    element: containerRef.current,
  });

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setLocalIsTransitioning(true);
      setTimeout(() => {
        onIndexChange(currentIndex - 1);
        setLocalIsTransitioning(false);
      }, 80);
    }
  };

  const handleNext = () => {
    if (currentIndex < validRenderUrls.length - 1) {
      setLocalIsTransitioning(true);
      setTimeout(() => {
        onIndexChange(currentIndex + 1);
        setLocalIsTransitioning(false);
      }, 80);
    }
  };

  // If no renders, show just the source image
  if (!validRenderUrls.length && sourceUrl) {
    return (
      <>
        <div
          ref={containerRef}
          className="w-full flex flex-col items-center justify-center"
        >
          <div
            ref={frameRef}
            data-image-frame
            className={`relative ${containerClassName} transition-[filter] duration-[600ms] ${isRendering ? 'saturate-[0.25] brightness-95' : ''} ${onImageClick ? 'cursor-pointer' : ''}`}
            style={containerStyle}
            onClick={() => {
              setSourceRect(viewerRectFromElement(frameRef.current));
              onImageClick?.();
            }}
          >
            <Image
              src={sourceUrl}
              alt={alt}
              fill
              sizes="(max-width: 768px) 100vw, 768px"
              // Box is the angle aspect → cover shows the angle full size.
              className="object-cover"
            />
          </div>
        </div>

        {sourceUrl && (
          <ImageViewer
            images={[{ src: sourceUrl, alt, isOriginalAngle: true }]}
            sourceRect={sourceRect}
            isOpen={
              isViewerOpenExternal !== undefined
                ? isViewerOpenExternal
                : isViewerOpen
            }
            onClose={() => {
              if (onViewerClose) {
                onViewerClose();
              } else {
                setIsViewerOpen(false);
              }
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div
        ref={containerRef}
        className="relative w-full flex flex-col items-center justify-center"
      >
        {/* Image container with comparison slider */}
        <div
          ref={frameRef}
          data-image-frame
          className={`relative ${containerClassName} transition-[filter] duration-[600ms] ${isRendering ? 'saturate-[0.25] brightness-95' : ''} ${onImageClick ? 'cursor-pointer' : ''}`}
          style={containerStyle}
          onClick={(e) => {
            if (isDraggingSlider || wasRecentlyDragging) return;
            if ((e.target as HTMLElement).closest('button, [role="button"], a')) return;
            setSourceRect(viewerRectFromElement(frameRef.current));
            onImageClick?.();
          }}
        >
          {sourceUrl && currentRenderUrl && (
            <div
              className="w-full h-full absolute inset-0"
              style={{
                opacity: isTransitioning ? 0.2 : 1,
                transition: isTransitioning
                  ? 'opacity 80ms ease-out'
                  : 'opacity 120ms ease-in',
              }}
            >
              <ImageComparisonSlider
                // Key on the SOURCE (angle) only — remount on angle
                // replacement, but NOT on render paging. Keying on the render
                // URL too tore the slider down on every carousel step, which
                // defeated its own per-URL anti-flash handling (reset only the
                // changed side, double-buffer the outgoing render) and flashed
                // the loading overlay instead of a clean render→render swap.
                key={sourceUrl}
                beforeImage={sourceUrl} // Source/angle on the left
                afterImage={currentRenderUrl} // Render on the right
                beforeAlt={alt}
                afterAlt={`${alt} - Variant ${currentIndex + 1}`}
                initialPosition={sliderPosition} // Use controlled or local position
                aspectRatio={aspectRatio}
                // Cover, not contain: container is the angle aspect, so the
                // angle fills it exactly and a mismatched render is cropped to
                // fill rather than letterboxed. Unified cross-platform rule.
                objectFit="cover"
                onPositionChange={handleSliderPositionChange}
                onDraggingChange={handleDraggingChange}
                className="w-full h-full"
                programmaticPosition={sliderProgrammaticPosition}
              />
            </div>
          )}

          {/* 4K badge for upscaled renders */}
          {isUpscaled && currentRenderUrl && (
            <div className="absolute top-2 left-2 z-10">
              <Badge
                variant="outline"
                className="bg-transparent text-primary border-primary text-chip px-2 py-0.5 backdrop-blur-sm"
              >
                4K
              </Badge>
            </div>
          )}
        </div>

        {/* Navigation buttons below the image */}
        {showNavigation && validRenderUrls.length > 1 && (
          <div className="flex justify-center items-center gap-2 mt-3">
            <button
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className="p-2 rounded-md bg-secondary hover:bg-secondary/80 disabled:bg-secondary/50 disabled:cursor-not-allowed transition-colors"
              title={tCommon('previousVariant')}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <span className="text-form-help text-muted-foreground px-2">
              {currentIndex + 1} / {validRenderUrls.length}
            </span>

            <button
              onClick={handleNext}
              disabled={currentIndex === validRenderUrls.length - 1}
              className="p-2 rounded-md bg-secondary hover:bg-secondary/80 disabled:bg-secondary/50 disabled:cursor-not-allowed transition-colors"
              title={tCommon('nextVariant')}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Image Viewer Modal — all renders with comparison support */}
      {(sourceUrl || currentRenderUrl) && (
        <ImageViewer
          images={
            validRenderUrls.length > 0
              ? validRenderUrls.map((url, i) => ({
                  src: url,
                  alt: `${alt} - Variant ${i + 1}`,
                  beforeSrc: sourceUrl, // enables comparison mode
                  comparisonPosition: sliderPosition, // open fullscreen at the card's handle position
                }))
              : sourceUrl
                ? [{ src: sourceUrl, alt, isOriginalAngle: true }]
                : []
          }
          initialIndex={currentIndex}
          sourceRect={sourceRect}
          isOpen={
            isViewerOpenExternal !== undefined
              ? isViewerOpenExternal
              : isViewerOpen
          }
          onClose={() => {
            if (onViewerClose) {
              onViewerClose();
            } else {
              setIsViewerOpen(false);
            }
          }}
        />
      )}
    </>
  );
}
